import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useFormStore, useAuthStore } from '../store';
import { PlusCircle, FileText, Edit2, Eye, Clock, ToggleLeft, ToggleRight, TrendingUp, Send, Calendar, CheckCircle, AlertCircle, Link2, User, Zap, ChevronDown, ChevronUp, Save, Wifi, WifiOff, Copy, Loader } from 'lucide-react';
import toast from 'react-hot-toast';

export default function DashboardPage() {
  const { forms, isLoading, fetchForms, toggleFormStatus } = useFormStore();
  const { user } = useAuthStore();
  const canCreate = ['super_admin', 'dept_admin'].includes(user?.role);
  const [stats, setStats] = useState(null);
  const [chartPeriod, setChartPeriod] = useState('7d');
  const [leadToken, setLeadToken] = useState('');
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [expandedLead, setExpandedLead] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [savingLead, setSavingLead] = useState(null);
  const [searchParams] = useSearchParams();
  const highlightRef = useRef(null);
  const [extConnected, setExtConnected] = useState(false);
  const [extToken, setExtToken] = useState('');
  const [pushingCrm, setPushingCrm] = useState(null);
  const [showExtSetup, setShowExtSetup] = useState(false);

  const handleToggle = async (form) => {
    try {
      const res = await toggleFormStatus(form.id);
      toast.success(`表單已${res.is_active ? '啟用' : '停用'}`);
    } catch {
      toast.error('操作失敗，請重試');
    }
  };

  useEffect(() => {
    fetchForms();
    axios.get('/api/submissions/stats')
      .then(res => setStats(res.data))
      .catch(() => {});
    axios.get('/api/auth/me')
      .then(res => setLeadToken(res.data.lead_token || ''))
      .catch(() => {});
    loadLeads();

    // 標記商機已讀
    axios.post('/api/submissions/mark-leads-read').catch(() => {});

    // 檢查 Extension 連線狀態
    axios.get('/api/extension/status').then(res => setExtConnected(res.data.connected)).catch(() => {});

    // 從通知連結自動展開：?lead=ID
    const leadId = searchParams.get('lead');
    if (leadId) setExpandedLead(leadId);
  }, []);

  const loadLeads = () => {
    setLeadsLoading(true);
    axios.get('/api/submissions', { params: { source: 'public_lead', limit: 50 } })
      .then(res => {
        setLeads(res.data.data || []);
        // 載入後自動捲到指定商機
        setTimeout(() => {
          if (highlightRef.current) {
            highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
      })
      .catch(() => {})
      .finally(() => setLeadsLoading(false));
  };

  const handleSaveStaffFields = async (lead) => {
    const staffData = editingData[lead.id];
    if (!staffData) return;
    setSavingLead(lead.id);
    try {
      await axios.put(`/api/submissions/${lead.id}`, { data: staffData });
      toast.success('已儲存');
      loadLeads();
      setEditingData(prev => { const n = { ...prev }; delete n[lead.id]; return n; });
    } catch { toast.error('儲存失敗'); }
    finally { setSavingLead(null); }
  };

  const updateStaffField = (leadId, fieldId, value) => {
    setEditingData(prev => ({
      ...prev,
      [leadId]: { ...(prev[leadId] || {}), [fieldId]: value },
    }));
  };

  const handlePushCRM = async (lead) => {
    setPushingCrm(lead.id);
    try {
      // 標記該筆為待同步，Extension 會拉取
      const res = await axios.post('/api/extension/queue-sync', {
        submissionId: lead.id,
      });
      if (res.data.success) {
        toast.success('已加入同步佇列，請在 Salesforce 開啟新增商機後，點 Extension「⚡ 同步填寫」');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      toast.error(`CRM 推送失敗: ${msg}`);
    } finally {
      setPushingCrm(null);
    }
  };

  const handleGenerateExtToken = async () => {
    try {
      const res = await axios.post('/api/extension/token');
      setExtToken(res.data.token);
      toast.success('Extension Token 已產生（有效期 30 天）');
    } catch {
      toast.error('產生 Token 失敗');
    }
  };

  const getSchema = (lead) => {
    if (!lead.form_schema) return { customerFields: [], staffFields: [] };
    const schema = typeof lead.form_schema === 'string' ? JSON.parse(lead.form_schema) : lead.form_schema;
    const all = schema.fields || [];
    return {
      customerFields: all.filter(f => !f.visibility || f.visibility === 'customer' || f.visibility === 'both'),
      staffFields: all.filter(f => f.visibility === 'staff'),
    };
  };


  return (
    <div style={{ padding: 32 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700 }}>表單管理</h1>
          <p style={{ color: 'var(--text-2)', fontSize: 14, marginTop: 4 }}>管理所有客戶資料收集表單</p>
        </div>
        {canCreate && (
          <Link to="/builder/new" className="btn btn-primary">
            <PlusCircle size={16} /> 新增表單
          </Link>
        )}
      </div>

      {/* 提示：每個表單可產生專屬客戶連結 */}
      {leadToken && (
        <div style={{
          padding: '10px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10,
          background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 10, fontSize: 12, color: '#065f46',
        }}>
          <Link2 size={14} />
          <span>每個表單可透過「分享連結」按鈕產生客戶填單頁面，客戶填完後會出現在下方「商機表單進件」</span>
        </div>
      )}

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14, marginBottom: 28 }}>
        {[
          { label: '表單總數', value: forms.length, color: '#1a56db', icon: <FileText size={18} /> },
          { label: '啟用中', value: forms.filter(f => f.is_active).length, color: '#10b981', icon: <Eye size={18} /> },
          { label: '已停用', value: forms.filter(f => !f.is_active).length, color: '#94a3b8', icon: <ToggleLeft size={18} /> },
          { label: '總提交數', value: stats?.total ?? '—', color: '#8b5cf6', icon: <Send size={18} /> },
          { label: '今日提交', value: stats?.today ?? '—', color: '#f59e0b', icon: <Calendar size={18} /> },
        ].map(stat => (
          <div key={stat.label} className="card" style={{ padding: '18px 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 26, fontWeight: 700, color: stat.color }}>{stat.value}</div>
                <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4 }}>{stat.label}</div>
              </div>
              <div style={{ color: stat.color, opacity: 0.6 }}>{stat.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {/* CRM Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14, marginBottom: 28, maxWidth: '50%' }}>
          {[
            { label: 'CRM 寫入成功', value: stats.crm?.success ?? '—', color: '#10b981', icon: <CheckCircle size={18} /> },
            { label: 'CRM 寫入失敗', value: stats.crm?.failed ?? '—', color: '#ef4444', icon: <AlertCircle size={18} /> },
          ].map(stat => (
            <div key={stat.label} className="card" style={{ padding: '18px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: stat.color }}>{stat.value}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4 }}>{stat.label}</div>
                </div>
                <div style={{ color: stat.color, opacity: 0.6 }}>{stat.icon}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Charts Row */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 28 }}>
          {/* Trend Bar Chart with period toggle */}
          <div className="card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <TrendingUp size={16} color="var(--primary)" />
                <span style={{ fontSize: 14, fontWeight: 700 }}>
                  {chartPeriod === '7d' ? '近 7 天提交趨勢' : '近 30 天提交趨勢'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {[{ key: '7d', label: '7天' }, { key: '30d', label: '30天' }].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setChartPeriod(key)}
                    className={`btn btn-sm ${chartPeriod === key ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ padding: '3px 10px', fontSize: 12 }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {(() => {
              const chartData = chartPeriod === '7d' ? (stats.weekly || []) : (stats.monthly || []);
              const chartMax = chartData.reduce((m, d) => Math.max(m, d.count), 1);
              return chartData.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text-3)', padding: '20px 0', fontSize: 13 }}>尚無資料</div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: chartPeriod === '30d' ? 2 : 6, height: 80 }}>
                  {chartData.map(d => (
                    <div key={d.date} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}
                      title={`${d.date}: ${d.count} 筆`}>
                      {chartPeriod === '7d' && (
                        <div style={{ fontSize: 10, color: 'var(--primary)', fontWeight: 700 }}>{d.count}</div>
                      )}
                      <div style={{
                        width: '100%', borderRadius: '3px 3px 0 0',
                        background: 'var(--primary)', opacity: 0.8,
                        height: `${Math.max(2, (d.count / chartMax) * 60)}px`,
                        transition: 'height 0.3s',
                      }} />
                      {chartPeriod === '7d' && (
                        <div style={{ fontSize: 9, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                          {new Date(d.date + 'T00:00:00').toLocaleDateString('zh-TW', { month: 'numeric', day: 'numeric' })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Top Forms */}
          <div className="card" style={{ padding: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>熱門表單（提交數）</div>
            {stats.byForm.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--text-3)', padding: '20px 0', fontSize: 13 }}>尚無資料</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {stats.byForm.map((f, i) => {
                  const maxCount = stats.byForm[0]?.count || 1;
                  return (
                    <div key={f.form_id}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 13 }}>
                        <span style={{ color: 'var(--text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>{f.title}</span>
                        <span style={{ color: 'var(--text-2)', fontWeight: 700, flexShrink: 0 }}>{f.count}</span>
                      </div>
                      <div style={{ height: 6, background: 'var(--surface-3)', borderRadius: 3 }}>
                        <div style={{ height: '100%', background: `hsl(${220 + i * 30}, 80%, 60%)`, borderRadius: 3, width: `${(f.count / maxCount) * 100}%`, transition: 'width 0.3s' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Form List */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700 }}>所有表單</h2>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-3)' }}>載入中...</div>
      ) : forms.length === 0 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center' }}>
          <FileText size={48} color="var(--text-3)" style={{ marginBottom: 12 }} />
          <p style={{ color: 'var(--text-2)', fontSize: 15 }}>尚未建立任何表單</p>
          {canCreate && <Link to="/builder/new" className="btn btn-primary" style={{ marginTop: 16, display: 'inline-flex' }}>建立第一個表單</Link>}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {forms.map(form => (
            <div key={form.id} className="card" style={{ padding: 18, display: 'flex', alignItems: 'center', gap: 14, transition: 'box-shadow 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--shadow)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = 'var(--shadow-sm)'}
            >
              <div style={{ width: 40, height: 40, background: 'var(--primary-light)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FileText size={18} color="var(--primary)" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{form.title}</span>
                  <span className={`badge ${form.is_active ? 'badge-green' : 'badge-gray'}`}>
                    {form.is_active ? '啟用中' : '已停用'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 14, marginTop: 3, fontSize: 12, color: 'var(--text-2)' }}>
                  {form.description && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>{form.description}</span>}
                  <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Clock size={11} /> {new Date(form.created_at).toLocaleDateString('zh-TW')}
                  </span>
                  {form.creator_name && <span>建立者：{form.creator_name}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                {form.is_active && leadToken && (
                  <button className="btn btn-sm" style={{ background: '#ecfdf5', color: '#059669', border: '1px solid #a7f3d0', display: 'flex', alignItems: 'center', gap: 4 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const formUrl = `${window.location.origin}/lead/${leadToken}/${form.id}`;
                      navigator.clipboard.writeText(formUrl);
                      toast.success('客戶填單連結已複製');
                    }}>
                    <Link2 size={13} /> 分享連結
                  </button>
                )}
                {form.is_active && (
                  <Link to={`/fill/${form.id}`} className="btn btn-secondary btn-sm">
                    <Eye size={14} /> 填寫
                  </Link>
                )}
                {canCreate && (
                  <Link to={`/builder/${form.id}`} className="btn btn-primary btn-sm">
                    <Edit2 size={14} /> 編輯
                  </Link>
                )}
                {user?.role === 'super_admin' && (
                  <button className="btn btn-secondary btn-sm" onClick={() => handleToggle(form)} title={form.is_active ? '停用表單' : '啟用表單'}>
                    {form.is_active ? <ToggleRight size={16} color="#10b981" /> : <ToggleLeft size={16} color="#94a3b8" />}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══ 商機表單進件 ═══ */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 32, marginBottom: 14 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700 }}>📝 商機表單進件</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{leads.length} 筆</span>
          <button className="btn btn-sm" onClick={() => setShowExtSetup(!showExtSetup)}
            style={{
              background: extConnected ? '#ecfdf5' : '#fff7ed',
              color: extConnected ? '#059669' : '#c2410c',
              border: `1px solid ${extConnected ? '#a7f3d0' : '#fed7aa'}`,
              display: 'flex', alignItems: 'center', gap: 4, fontSize: 11,
            }}>
            {extConnected ? <Wifi size={12} /> : <WifiOff size={12} />}
            {extConnected ? 'Extension 已連線' : 'Extension 設定'}
          </button>
        </div>
      </div>

      {/* Extension 設定面板 */}
      {showExtSetup && (
        <div className="card" style={{ padding: 16, marginBottom: 14, background: '#fefce8', border: '1px solid #fde68a' }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: '#92400e' }}>🔌 Chrome Extension CRM 橋接器設定</div>
          <div style={{ fontSize: 12, color: '#78350f', marginBottom: 10 }}>
            使用 Chrome Extension 可直接在瀏覽器中操作 CRM 頁面（Salesforce / HubSpot / Zoho / 自訂系統），無需 API。
          </div>
          <ol style={{ fontSize: 12, color: '#78350f', paddingLeft: 20, lineHeight: 1.8, marginBottom: 12 }}>
            <li>安裝 Chrome Extension（載入 <code>crm-extension/</code> 資料夾）</li>
            <li>產生 Token 並貼入 Extension Popup</li>
            <li>Extension 狀態指示燈變綠即完成</li>
          </ol>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-sm btn-primary" onClick={handleGenerateExtToken}
              style={{ fontSize: 12 }}>
              🔑 產生 Extension Token
            </button>
            {extToken && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
                <input readOnly value={extToken}
                  style={{
                    flex: 1, padding: '5px 8px', fontSize: 11, fontFamily: 'monospace',
                    background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
                  }} />
                <button className="btn btn-sm" style={{ padding: '5px 8px' }}
                  onClick={() => { navigator.clipboard.writeText(extToken); toast.success('Token 已複製'); }}>
                  <Copy size={12} />
                </button>
              </div>
            )}
            <button className="btn btn-sm" style={{ fontSize: 12 }}
              onClick={() => { axios.get('/api/extension/status').then(res => { setExtConnected(res.data.connected); toast(res.data.connected ? '已連線 ✅' : '未連線 ❌'); }).catch(() => toast.error('檢查失敗')); }}>
              🔄 檢查連線
            </button>
          </div>
        </div>
      )}

      {leadsLoading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-3)' }}>載入中...</div>
      ) : leads.length === 0 ? (
        <div className="card" style={{ padding: 40, textAlign: 'center' }}>
          <FileText size={36} color="var(--text-3)" style={{ marginBottom: 8 }} />
          <p style={{ color: 'var(--text-2)', fontSize: 14 }}>尚無客戶填單資料</p>
          <p style={{ color: 'var(--text-3)', fontSize: 12, marginTop: 4 }}>透過表單「分享連結」讓客戶填寫，資料會出現在這裡</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {leads.map(lead => {
            const isExpanded = expandedLead === lead.id;
            const { customerFields, staffFields } = getSchema(lead);
            const data = lead.data || {};
            const staffEdit = editingData[lead.id] || {};
            // 取得顯示名稱（從 customer fields 中找姓名欄位）
            const displayName = Object.entries(data).map(([k, v]) => {
              const f = customerFields.find(cf => cf.id === k);
              return f && (f.label.includes('姓') || f.label.includes('名')) ? v : null;
            }).filter(Boolean).join('') || lead.form_title || '未命名';

            return (
              <div key={lead.id} ref={lead.id === expandedLead ? highlightRef : null}
                className="card" style={{
                  padding: 0, overflow: 'hidden',
                  boxShadow: lead.id === searchParams.get('lead') ? '0 0 0 2px #1a56db' : undefined,
                }}>
                <div style={{
                  padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
                }} onClick={() => setExpandedLead(isExpanded ? null : lead.id)}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                    background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <User size={16} color="#059669" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {displayName}
                      <span className="badge badge-green" style={{ marginLeft: 8, fontSize: 10 }}>{lead.form_title}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--text-2)', marginTop: 2, flexWrap: 'wrap' }}>
                      {customerFields.slice(0, 4).map(f => {
                        const v = data[f.id];
                        return v ? <span key={f.id}>{f.label}: {v}</span> : null;
                      }).filter(Boolean)}
                    </div>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--text-3)', flexShrink: 0 }}>
                    {new Date(lead.submitted_at).toLocaleDateString('zh-TW', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                  {isExpanded ? <ChevronUp size={16} color="var(--text-3)" /> : <ChevronDown size={16} color="var(--text-3)" />}
                </div>
                {isExpanded && (
                  <div style={{ padding: '0 18px 16px', borderTop: '1px solid var(--border)' }}>
                    {/* 客戶填寫欄位（唯讀） */}
                    {customerFields.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', marginBottom: 8 }}>客戶填寫</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
                          {customerFields.map(f => (
                            <div key={f.id} style={{ fontSize: 13, padding: '4px 0' }}>
                              <span style={{ color: 'var(--text-3)' }}>{f.label}：</span>
                              <span style={{ fontWeight: 500 }}>{data[f.id] || '—'}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 業務填寫欄位（可編輯） */}
                    {staffFields.length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', marginBottom: 8 }}>業務填寫</div>
                        <div style={{ display: 'grid', gap: 8 }}>
                          {staffFields.map(f => {
                            const currentVal = staffEdit[f.id] ?? data[f.id] ?? '';
                            return (
                              <div key={f.id}>
                                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 4, display: 'block' }}>
                                  {f.label}{f.required && <span style={{ color: '#ef4444' }}> *</span>}
                                </label>
                                {f.type === 'select' ? (
                                  <select className="input" value={currentVal}
                                    onChange={e => updateStaffField(lead.id, f.id, e.target.value)}
                                    style={{ fontSize: 13 }}>
                                    <option value="">請選擇</option>
                                    {(f.options || []).map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
                                  </select>
                                ) : f.type === 'checkbox' ? (
                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    {(f.options || []).map((opt, i) => (
                                      <label key={i} style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                                        <input type="checkbox" checked={(currentVal || '').split(',').includes(opt)}
                                          onChange={e => {
                                            const arr = (currentVal || '').split(',').filter(Boolean);
                                            if (e.target.checked) arr.push(opt); else arr.splice(arr.indexOf(opt), 1);
                                            updateStaffField(lead.id, f.id, arr.join(','));
                                          }} />
                                        {opt}
                                      </label>
                                    ))}
                                  </div>
                                ) : f.type === 'date' ? (
                                  <input type="date" className="input" value={currentVal}
                                    onChange={e => updateStaffField(lead.id, f.id, e.target.value)}
                                    style={{ fontSize: 13 }} />
                                ) : (
                                  <input className="input" value={currentVal}
                                    onChange={e => updateStaffField(lead.id, f.id, e.target.value)}
                                    placeholder={f.placeholder || ''} style={{ fontSize: 13 }} />
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <button onClick={() => handleSaveStaffFields(lead)}
                          disabled={savingLead === lead.id || !editingData[lead.id]}
                          className="btn btn-primary btn-sm" style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Save size={13} /> {savingLead === lead.id ? '儲存中...' : '儲存業務欄位'}
                        </button>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 8, marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)', alignItems: 'center' }}>
                      <button className="btn btn-sm"
                        disabled={pushingCrm === lead.id}
                        onClick={() => handlePushCRM(lead)}
                        style={{
                          background: '#eef2ff',
                          color: '#4f46e5',
                          border: '1px solid #c7d2fe',
                          display: 'flex', alignItems: 'center', gap: 4,
                        }}>
                        {pushingCrm === lead.id
                          ? <><Loader size={13} className="spin" /> 排隊中...</>
                          : <><Zap size={13} /> 推送至 CRM</>}
                      </button>
                      <span style={{ fontSize: 11, color: '#6366f1', display: 'flex', alignItems: 'center', gap: 3 }}>
                        在 Extension 點「⚡ 同步填寫」
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
